<template>
    <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-40" :class="{ 'hidden': !isBlur }" @click="handleOverlayClick"></div>
    <section
        class="bg-white shadow-sm px-3 md:px-6 py-2 md:py-4 flex justify-between items-center w-full z-40 min-h-[64px] md:h-[70px] fixed border-b border-gray-100 dark:bg-dark-900 dark:border-dark-700">
        <div class="flex items-center gap-2 md:gap-4 min-w-0">
            <!-- Hamburger Menu -->
            <div id="open-sidebar" class="bg-blue-100 p-2 rounded-lg cursor-pointer hover:bg-blue-200 transition-colors duration-200 dark:bg-dark-700 dark:hover:bg-dark-600"
                @click="toggleSidebar">
                <Bars3Icon class="h-5 w-5 text-blue-600 dark:text-blue-300" />
            </div>
            
            <!-- Date and Time -->
            <div class="hidden md:flex items-center gap-4">
                <div class="flex items-center gap-2 text-gray-600 dark:text-dark-300">
                    <CalendarDaysIcon class="h-4 w-4" />
                    <span class="text-sm font-medium">{{ currentDate }}</span>
                </div>
                <div class="flex items-center gap-2 text-gray-600 dark:text-dark-300">
                    <ClockIcon class="h-4 w-4" />
                    <span class="text-sm font-medium">{{ currentTime }}</span>
                </div>
            </div>

            <div class="md:hidden flex items-center gap-1.5 text-[11px] font-medium text-gray-600 dark:text-dark-300 min-w-0">
                <span class="flex items-center gap-0.5 shrink-0">
                    <CalendarDaysIcon class="h-3.5 w-3.5" />
                    <span>{{ shortDate }}</span>
                </span>
                <span class="text-gray-300 dark:text-dark-600 shrink-0">|</span>
                <span class="flex items-center gap-0.5 shrink-0">
                    <ClockIcon class="h-3.5 w-3.5" />
                    <span>{{ currentTime }}</span>
                </span>
            </div>
        </div>
        
        <div class="flex items-center gap-2 md:gap-4">
            <button
                id="dark-mode-toggle"
                type="button"
                class="group relative inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-100 px-2.5 md:px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all duration-200 hover:bg-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-dark-100 dark:hover:bg-dark-600"
                :aria-pressed="isDarkMode"
                :title="isDarkMode ? 'Aktifkan mode terang' : 'Aktifkan mode gelap'"
                @click="toggleDarkMode"
            >
                <span class="relative block h-5 w-10 rounded-full transition-colors duration-200"
                    :class="isDarkMode ? 'bg-blue-600' : 'bg-gray-300 dark:bg-dark-500'">
                    <span class="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all duration-200"
                        :class="isDarkMode ? 'left-5' : 'left-0.5'"></span>
                </span>
                <span class="flex items-center gap-1">
                    <SunIcon v-if="!isDarkMode" class="h-4 w-4 text-amber-500" />
                    <MoonIcon v-else class="h-4 w-4 text-blue-300" />
                    <span class="hidden sm:inline">{{ isDarkMode ? 'Dark' : 'Light' }}</span>
                </span>
            </button>

            <!-- Open Order Status -->
            <div class="hidden sm:flex items-center gap-2">
                <div class="w-2 h-2 bg-green-500 rounded-full"></div>
                <span class="text-sm font-medium text-gray-700 dark:text-dark-200">Open Order</span>
            </div>
            
            <!-- Profile and Logout -->
            <div class="profile relative">
                <span class="flex items-center">
                    <div id="profile-button" @click="toggleProfileDropdown" class="cursor-pointer">
                        <UserCircleIcon
                            class="h-7 w-7 md:h-8 md:w-8 text-gray-400 rounded-full transition-opacity duration-200 hover:opacity-90 dark:text-dark-300" />
                    </div>
                </span>
                <div v-if="isProfileDropdownOpen"
                    class="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden dark:bg-dark-800 dark:border-dark-600">
                    <RouterLink id="url-profile" to="/profil"
                        class="px-4 py-3 text-sm flex hover:bg-gray-50 text-gray-700 transition-colors duration-200 dark:text-dark-100 dark:hover:bg-dark-700">
                        <UserIcon class="me-2 h-4 w-4" /> Profil
                    </RouterLink>
                    <button id="logout" @click="logout"
                        class="w-full flex text-left px-4 py-3 text-sm hover:bg-gray-50 text-gray-700 transition-colors duration-200 dark:text-dark-100 dark:hover:bg-dark-700">
                        <ArrowRightOnRectangleIcon class="h-4 w-4 me-2" /> Logout
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { UserCircleIcon, UserIcon, ArrowRightOnRectangleIcon, Bars3Icon, CalendarDaysIcon, ClockIcon, SunIcon, MoonIcon } from '@heroicons/vue/24/solid';
import { mapActions, mapGetters } from 'vuex';

export default {
    name: 'TopBar',
    components: {
        UserCircleIcon, UserIcon, ArrowRightOnRectangleIcon, Bars3Icon, CalendarDaysIcon, ClockIcon, SunIcon, MoonIcon
    },
    data() {
        return {
            isProfileDropdownOpen: false,
            isBlur: false,
            outlet_id: localStorage.getItem('outlet_id'),
            currentTime: '',
            currentDate: '',
            shortDate: '',
        }
    },
    computed: {
        ...mapGetters('darkMode', ['isDarkMode']),
        ...mapGetters('outlets', ['allOutlets']),
        outlet_name() {
            const outlet = this.allOutlets.find(outlet => outlet.id == this.outlet_id);
            return outlet ? outlet.name : false;
        }
    },
    methods: {
        ...mapActions('darkMode', ['toggleDarkMode', 'initDarkMode']),
        toggleSidebar() {
            this.$parent.$refs.sidebar?.toggleSidebar();
        },
        toggleProfileDropdown() {
            this.isProfileDropdownOpen = !this.isProfileDropdownOpen;
        },
        closeProfileDropdown() {
            this.isProfileDropdownOpen = false;
        },
        handleOverlayClick() {
            this.$parent.$refs.sidebar?.closeSidebar();
        },
        logout() {
            localStorage.clear();
            this.$router.push('/');
        },
        updateDateTime() {
            const now = new Date();
            this.currentTime = now.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true 
            });
            this.currentDate = now.toLocaleDateString('en-US', { 
                weekday: 'short', 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric' 
            });
            this.shortDate = now.toLocaleDateString('en-US', {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
            });
        }
    },
    mounted() {
        this.initDarkMode();
        this.$store.dispatch('outlets/fetchOutlets');
        this.updateDateTime();
        setInterval(this.updateDateTime, 1000);
    },
}
</script>

<style>
/* Modern navbar styles - no custom styles needed */
</style>